import React, { PropsWithChildren, Ref } from 'react';
import {
   getNextLargerBreakpoint,
   getNextSmallerBreakpoint,
   useCombinedRefs,
   useContainerBreakpoints
} from '../../../../hooks';
import {
   GridTemplateDefinition,
   GridTemplatePanel,
} from './GridTemplatePanel';
import { FlexTemplateDefinition, FlexTemplatePanel } from './FlexTemplatePanel';
import { TemplatePanelProps } from '../TemplatePanel';

export type BreakpointWidth = 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

export type TemplateDefinition = GridTemplateDefinition | FlexTemplateDefinition;

export type ResponsiveTemplateDefinition = {
   [breakpointName in BreakpointWidth]?: TemplateDefinition | BreakpointWidth
}

export interface ResponsiveTemplatePanelProps extends Omit<TemplatePanelProps, 'template'> {
   /**
    * The template to use.
    */
   template: ResponsiveTemplateDefinition;
}

export type ResponsiveTemplatePanelPropsWithChildren = PropsWithChildren<ResponsiveTemplatePanelProps>;

export const ResponsiveTemplatePanel = React.forwardRef<HTMLDivElement, ResponsiveTemplatePanelPropsWithChildren>(
   (props: ResponsiveTemplatePanelPropsWithChildren, ref: Ref<HTMLDivElement>) => {

      const responsiveTemplate = props.template;

      const [dimensionsRef, dimensions, currentBreakpoint] = useContainerBreakpoints();

      const combinedRef = useCombinedRefs(dimensionsRef as Ref<HTMLDivElement>,
                                          ref as Ref<HTMLDivElement>);

      let breakpointNameToUse = currentBreakpoint;
      if (breakpointNameToUse == null || responsiveTemplate[breakpointNameToUse] === undefined) {
         breakpointNameToUse = findClosestBreakpoint(responsiveTemplate, breakpointNameToUse);
      }

      const template = breakpointNameToUse ? getTemplateDefinition(responsiveTemplate, breakpointNameToUse) : undefined;
      if (!template) {
         throw Error("Template Definition not recognized");
      }

      if (isGridTemplateDefinition(template)) {
         return <GridTemplatePanel
            {...props}
            template={template as GridTemplateDefinition}
            ref={combinedRef}
         >
            {props.children}
         </GridTemplatePanel>
      }
      else {
         return <FlexTemplatePanel
            {...props}
            template={template as FlexTemplateDefinition}
            ref={combinedRef}
         >
            {props.children}
         </FlexTemplatePanel>
      }
   }
);

function isGridTemplateDefinition(obj: any): obj is GridTemplateDefinition {
   return obj && obj.columns !== undefined;
}

function isTemplateDefinition(obj: any): obj is TemplateDefinition {
   return obj && obj.rows !== undefined;
}

const getTemplateDefinition = (responsiveTemplate: ResponsiveTemplateDefinition,
                               breakpointNameToUse: BreakpointWidth,
                               count?: number): TemplateDefinition => {

   const templateToUse = responsiveTemplate[breakpointNameToUse];
   if (isTemplateDefinition(templateToUse)) {
      return templateToUse;
   }

   if (count !== undefined && count > 9) {
      throw Error("There was a problem with the responsive template definition");
   }

   return getTemplateDefinition(responsiveTemplate,
                                templateToUse as BreakpointWidth,
                                (count || 1) + 1);
};

const findClosestBreakpoint = (responsiveTemplate: ResponsiveTemplateDefinition,
                               breakpointNameToUse?: BreakpointWidth | null): BreakpointWidth | null => {

   if (breakpointNameToUse == null) {
      const keys = Object.keys(responsiveTemplate);
      if (keys && keys.length > 0) {
         breakpointNameToUse = Object.keys(responsiveTemplate)[keys.length - 1] as BreakpointWidth;
      }

   }

   if (breakpointNameToUse != null) {
      let nextLargerBreakpoint: BreakpointWidth | null = breakpointNameToUse;
      while (nextLargerBreakpoint != null && responsiveTemplate[nextLargerBreakpoint] === undefined) {
         nextLargerBreakpoint = getNextLargerBreakpoint(nextLargerBreakpoint);
      }

      if (nextLargerBreakpoint == null) {
         let nextSmallerBreakpoint = getNextSmallerBreakpoint(breakpointNameToUse);
         while (nextSmallerBreakpoint != null && responsiveTemplate[nextSmallerBreakpoint] === undefined) {
            nextSmallerBreakpoint = getNextSmallerBreakpoint(nextSmallerBreakpoint);
         }

         if (nextSmallerBreakpoint != null) {
            breakpointNameToUse = nextSmallerBreakpoint;
         }
      }
      else {
         breakpointNameToUse = nextLargerBreakpoint;
      }
   }

   return breakpointNameToUse || null;
};
